/**
 * WorkflowAIPanel — Sidebar de chat IA para criar/modificar workflows.
 *
 * Usa o ChatPanel compartilhado e envia mensagens para
 * POST /api/workflows/:id/ai-modify.
 */

import React, { useCallback, useRef, useState } from 'react';
import { Icon } from '@/shared/icons';
import { ChatPanel, type ChatMessage } from '@/shared/components/ui/ChatPanel';
import { api } from '@/lib/api';
import type { WorkflowDefinition } from '@troia-v3/types';

interface WorkflowAIPanelProps {
  workflowId: string;
  currentDefinition: WorkflowDefinition;
  onDefinitionUpdate: (definition: WorkflowDefinition) => void;
  onClose: () => void;
}

interface AIModifyResponse {
  updatedDefinition: WorkflowDefinition;
  assistantMessage: string;
  changes: {
    nodesAdded: number;
    nodesModified: number;
    nodesRemoved: number;
    edgesAdded: number;
    edgesRemoved: number;
  };
}

function formatChanges(changes: AIModifyResponse['changes']): string | undefined {
  const parts: string[] = [];
  if (changes.nodesAdded > 0) parts.push(`+${changes.nodesAdded} node${changes.nodesAdded > 1 ? 's' : ''}`);
  if (changes.nodesModified > 0) parts.push(`${changes.nodesModified} modificado${changes.nodesModified > 1 ? 's' : ''}`);
  if (changes.nodesRemoved > 0) parts.push(`-${changes.nodesRemoved} removido${changes.nodesRemoved > 1 ? 's' : ''}`);
  if (changes.edgesAdded > 0) parts.push(`+${changes.edgesAdded} conexão${changes.edgesAdded > 1 ? 'ões' : ''}`);
  return parts.length > 0 ? parts.join(' · ') : undefined;
}

export const WorkflowAIPanel: React.FC<WorkflowAIPanelProps> = ({
  workflowId,
  currentDefinition,
  onDefinitionUpdate,
  onClose,
}) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const msgCounter = useRef(0);

  const makeMsg = (role: 'assistant' | 'user', content: string, badge?: string): ChatMessage => ({
    id: `wf-msg-${++msgCounter.current}`,
    role,
    content,
    badge,
  });

  const handleSendMessage = useCallback(async (userMessage: string) => {
    // Add user message to chat
    const userMsg = makeMsg('user', userMessage);
    setMessages((prev) => [...prev, userMsg]);
    setIsLoading(true);

    try {
      // Build chat history (without badges, just role + content)
      const chatHistory = messages.map((m) => ({ role: m.role, content: m.content }));

      const response = await api.post(`workflows/${workflowId}/ai-modify`, {
        message: userMessage,
        chatHistory,
        currentDefinition,
      });

      const data = response.data.data as AIModifyResponse;

      // Add assistant response
      const badge = formatChanges(data.changes);
      setMessages((prev) => [...prev, makeMsg('assistant', data.assistantMessage, badge)]);

      // Update canvas
      onDefinitionUpdate(data.updatedDefinition);
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Erro ao processar sua solicitação.';
      setMessages((prev) => [...prev, makeMsg('assistant', `Desculpe, ocorreu um erro: ${msg}`)]);
    } finally {
      setIsLoading(false);
    }
  }, [workflowId, currentDefinition, messages, onDefinitionUpdate]);

  return (
    <div
      className="flex flex-col md:w-[360px] md:max-w-full md:flex-shrink-0 md:border-l md:h-full max-md:fixed max-md:inset-x-0 max-md:bottom-0 max-md:z-[70] max-md:max-h-[75vh] max-md:rounded-t-2xl max-md:border-t max-md:shadow-[0_-8px_30px_rgba(0,0,0,0.18)] max-md:[background:var(--theme-overlay-card-background)] md:[background-color:var(--theme-card-background)]"
      style={{
        borderColor: 'var(--theme-card-border)',
      }}
    >
      {/* Header */}
      <div
        className="flex items-center gap-3 px-4 py-3 flex-shrink-0"
        style={{ borderBottom: '1px solid var(--theme-card-border)' }}
      >
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: 'var(--theme-primary)' }}
        >
          <Icon name="cpu" className="w-4 h-4" style={{ color: 'var(--theme-button-primary-text)' }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold" style={{ color: 'var(--theme-text)' }}>
            Assistente IA
          </div>
          <div className="text-[10px]" style={{ color: 'var(--theme-muted-text)' }}>
            Crie e modifique o workflow por chat
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="p-1.5 rounded-lg transition-colors"
          style={{ color: 'var(--theme-muted-text)' }}
          onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--theme-card-text)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--theme-muted-text)'; }}
        >
          <Icon name="x" className="w-4 h-4" />
        </button>
      </div>

      {/* Chat */}
      <div className="flex-1 overflow-hidden">
        <ChatPanel
          messages={messages}
          onSendMessage={handleSendMessage}
          isLoading={isLoading}
          hideHeader
          placeholder="Ex: Crie um fluxo de qualificação de lead..."
          emptyMessage="Descreva o workflow que deseja criar ou as mudanças que quer fazer. Eu vou gerar os nodes automaticamente."
          assistantIcon="cpu"
        />
      </div>
    </div>
  );
};

export default WorkflowAIPanel;
